import type {
  CategoryKey,
  RegistrationRequest,
} from "@/features/public/types/public";

type BuildRegistrationPayloadInput = {
  categoryKey: CategoryKey;
  submittedValues: RegistrationRequest;
};

const normalizeIdentity = (value?: string) =>
  (value || "").replace(/[-\s]+/g, "").toUpperCase();

const normalizeMyKad = (value?: string) => (value || "").replace(/\D/g, "");

const normalizeOptionalText = (value: unknown) => {
  const text = String(value || "").trim();
  return text.length > 0 ? text : null;
};

const toOptionalNumber = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const digits = String(value || "").replace(/[^\d]/g, "");
  return digits ? Number(digits) : null;
};

const deriveAgeFromMyKad = (myKadRaw: string) => {
  const myKad = normalizeMyKad(myKadRaw);
  if (!/^\d{12}$/.test(myKad)) return undefined;

  const yy = Number(myKad.slice(0, 2));
  const mm = Number(myKad.slice(2, 4));
  const dd = Number(myKad.slice(4, 6));

  const today = new Date();
  const currentYY = today.getFullYear() % 100;
  const fullYear = yy > currentYY ? 1900 + yy : 2000 + yy;

  const dob = new Date(fullYear, mm - 1, dd);
  if (
    dob.getFullYear() !== fullYear ||
    dob.getMonth() !== mm - 1 ||
    dob.getDate() !== dd
  ) {
    return undefined;
  }

  let age = today.getFullYear() - fullYear;
  const hasBirthdayPassed =
    today.getMonth() > dob.getMonth() ||
    (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());

  if (!hasBirthdayPassed) age -= 1;
  return age >= 0 ? age : undefined;
};

const isAgeConsistentWithBirthYear = (age: number, yearOfBirth: number) => {
  const currentYear = new Date().getFullYear();
  const ageIfBirthdayPassed = currentYear - yearOfBirth;
  const ageIfNotYetBirthday = ageIfBirthdayPassed - 1;
  return age === ageIfBirthdayPassed || age === ageIfNotYetBirthday;
};

function buildRegistrationPayload({
  categoryKey,
  submittedValues,
}: BuildRegistrationPayloadInput): RegistrationRequest {
  const address1 = String(submittedValues.address1 || "").trim();
  const address2 = normalizeOptionalText(submittedValues.address2);

  return {
    ...submittedValues,
    mdCategoryKey: categoryKey,
    age: Number(submittedValues.age),
    yearOfBirth: Number(submittedValues.yearOfBirth),
    address1,
    address2,
    totalMotorcycle: toOptionalNumber(submittedValues.totalMotorcycle),
    totalCar: toOptionalNumber(submittedValues.totalCar),
    totalElectricVehicle: toOptionalNumber(
      submittedValues.totalElectricVehicle,
    ),
    registrationRemark: normalizeOptionalText(
      submittedValues.registrationRemark,
    ),
  };
}

export {
  normalizeIdentity,
  normalizeMyKad,
  normalizeOptionalText,
  toOptionalNumber,
  deriveAgeFromMyKad,
  isAgeConsistentWithBirthYear,
  buildRegistrationPayload,
};
